// @ts-nocheck
import React, {
  useCallback, useState, useEffect, useRef,
} from 'react';
import { styled } from '@mui/material/styles';
import Alert from '@mui/material/Alert';
import MUIButton from '@mui/material/Button';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { toast } from 'react-toastify';
import css from '../shared/css';
import Button from '../shared/button';
import PermissionLayers from '../permission-layers';
import type { Layer } from '../models/layer';
import type { Role } from '../models/role';
import { MapView, convertFulcrumLayerToMapLayer } from '../shared/map';
import ArcGisMapView from '../shared/map/arc-gis-map-view';
import SearchPlugin from '../shared/map/plugins/search';
import type { App } from '../hooks/use-app';
import { useApp } from '../hooks/use-app';
import LayerSettings from '../models/layer-settings';
import DefaultPlugins from '../shared/map/plugins/default';
import Basemap from '../shared/map/plugins/basemap';
import PreviewLayer from '../shared/map/plugins/preview-layer';
import LayerSettingsReferenceHandler from '../shared/map/plugins/utils/layer-settings-reference-handler';
import FileLayer from './file-layer';
import JsonLayer from './json-layer';
import FeatureServiceLayer from './feature-service-layer';
import WMSLayer from './wms-layer';
import { FulcrumLayer } from './fulcrum-layer';
import type { PickableApp } from './fulcrum-layer';
import XYZLayer from './xyz-layer';

interface Props {
  originalLayer: Layer;
  layerTypes: string[][];
  apps: PickableApp[];
  isNew: boolean;
  roles: Role[];
}

const LayerTypeInfoIcon = styled(InfoOutlinedIcon)`
  color: #5856D6;
  position: relative;
  top: calc(50% - 12px);
`;

const LayerTypeAlert = styled(Alert)`
  padding: 16px;
  background: linear-gradient(0deg, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.9)), #5856D6;
  margin: 12px 0px;
  width: 446px;
  color: #232256;
`;

const FSLButton = styled(MUIButton)`
  background: linear-gradient(0deg, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.9)), #5856D6;
  font-style: normal;
  font-weight: 500;
  font-size: 13px;
  line-height: 22px;
  color: #232256;
  letter-spacing: 0.46px;
  padding: 0;
  text-transform: uppercase;
  transition: none;
  top: 30px;
  &:hover{
    background: rgba(88, 86, 214, 0.04);
    color: #232256;
  }
  width: 100px;
  min-width: auto;
`;

const fileLayerConfig = {
  kml: {
    acceptedFileTypes: '.kml,.kmz',
    acceptedFilesName: 'KML/KMZ',
  },
  mbtiles: {
    acceptedFileTypes: '.mbtiles',
    acceptedFilesName: 'MBTiles',
  },
  mmpk: {
    acceptedFileTypes: '.mmpk',
    acceptedFilesName: 'MMPK',
  },
};

const nameFromFileName = (fileName: string) => fileName.replace(/\.(mbtiles|kml|kmz|mmpk)$/i, '');

const LayerUploader: React.FC<Props> = ({
  originalLayer,
  layerTypes,
  apps,
  isNew,
  roles,
}: Props) => {
  const app = useApp();
  const [layer, setLayer] = useState(originalLayer);
  const [previewLayer, setPreviewLayer] = useState(originalLayer);
  const [mapViewLayers, setMapViewLayers] = useState(getMapViewLayers(originalLayer, app));
  const [disabled, setDisabled] = useState(false);
  const [sending, setSending] = useState(false);
  const [toastId, setToastId] = useState(undefined);
  const layerRef = useRef(layer);

  useEffect(() => {
    layerRef.current = layer;
  }, [layer]);

  useEffect(() => {
    const validInputName = (layer.name || '').length > 0 && layer.name.trim().length > 0;
    setDisabled(!validInputName || sending);
  }, [layer.name, sending]);

  const setLayerSource = useCallback((source: string) => {
    setLayer({ ...layer, source });
  }, [setLayer, layer]);

  const setLayerFile = useCallback((key: string, fileName: string, fileSize = 0) => {
    setLayer((currentLayer) => {
      const currentName = currentLayer.name || '';
      const name = currentName.trim().length > 0 ? currentName : nameFromFileName(fileName);

      const updatedLayer = {
        ...currentLayer, key, file_name: fileName, file_size: fileSize, name,
      };
      layerRef.current = updatedLayer;
      return updatedLayer;
    });
  }, [setLayer]);

  const uploadBegin = () => {
    setSending(true);
  };

  const uploadComplete = () => {
    setSending(false);
    setDisabled(false);
    toast.dismiss(toastId);
    setToastId(undefined);
  };

  const onLayerError = () => {
    setDisabled(false);
    setSending(false);
  };

  const renderLayerImporter = (startUpload: boolean) => {
    const layerType = layer.type || '';
    const fileUploaderProps = {
      startUpload,
      onUploadBegin: uploadBegin,
      onComplete: uploadComplete,
      onError: onLayerError,
      maxFiles: 1,
    };

    const fileLayer = fileLayerConfig[layerType.toLowerCase()];

    if (fileLayer) {
      return (
        <FileLayer
          key={layerType.toLowerCase()}
          isNew={isNew}
          fileUploaderProps={fileUploaderProps}
          setLayerFile={setLayerFile}
          apiToken={app.api?.token}
          currentFileName={layer.file_name}
          currentFileSize={layer.file_size}
          acceptedFileTypes={fileLayer.acceptedFileTypes}
          acceptedFilesName={fileLayer.acceptedFilesName}
        />
      );
    }

    switch (layerType.toLowerCase()) {
      case 'fulcrum': {
        return (
          <FulcrumLayer
            apps={apps}
            layerSource={layer.source}
            setLayerSource={setLayerSource}
          />
        );
      }
      case 'xyz': {
        return (<XYZLayer layerSource={layer.source} setLayerSource={setLayerSource} />);
      }
      case 'feature-service': {
        return (<FeatureServiceLayer layerSource={layer.source} setLayerSource={setLayerSource} />);
      }
      case 'wms': {
        return (<WMSLayer layerSource={layer.source} setLayerSource={setLayerSource} />);
      }
      case 'tilejson': {
        return (<JsonLayer title="URL or TileJSON" defaultValue={layer.source} />);
      }
      case 'geojson': {
        return (<JsonLayer title="URL or GeoJSON" defaultValue={layer.source} httpsOnly />);
      }
      default: {
        return null;
      }
    }
  };

  const layerName = layer.name || '';

  const saveLayer = (e) => {
    e.preventDefault();
    setDisabled(true);
    setSending(true);
    document.getElementsByClassName('form-stacked')[0].submit();
  };

  const handleUpdateLayerType = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLayer({
      ...layer,
      type: e.target.value,
      source: null,
      file_size: 0,
      key: null,
      file_name: null,
    });
    setMapViewLayers([]);
  };

  const updatePreview = () => {
    const newMapViewLayers = getMapViewLayers(layer, app);
    if (newMapViewLayers.length || layer?.type === 'feature-service') {
      setPreviewLayer({ ...layer, id: `${previewLayer.id}-` });
      setMapViewLayers({ ...newMapViewLayers });
    }
  };

  const renderLayerTypeSelect = () => (
    <select
      data-testid="new-layer-type-select"
      id="layer_layer_type"
      name="layer[type]"
      required
      className={styles.formControl}
      value={layer.type || ''}
      onChange={handleUpdateLayerType}
    >
      <option value="">Select Layer Type</option>
      {
        layerTypes
          .filter((layerType) => layerType[0] !== 'all')
          .filter((layerType) => layerType[0] !== 'kml' || app.globals.ldflags['kml-layers'])
          .filter((layerType) => layerType[0] !== 'mmpk' || app.globals.ldflags['arcgis-mmpk-layers'])
          .map((layerType) => (
            <option key={layerType[0]} value={layerType[0]}>{layerType[1]}</option>
          ))
      }
    </select>
  );

  const renderLayerType = () => (
    <div data-testid="uploaded-layer-type" id="layer_layer_type" className="layer-type input">
      {layerTypes.find((layerType) => layerType[0] === layer.type)?.[1] || ''}
    </div>
  );

  const renderLoadingState = () => {
    if (sending && !toastId) {
      const message = isNew ? 'Processing new map layer' : 'Updating map layer';
      setToastId(toast.loading(message));
    }

    return <></>;
  };

  const renderFeatureServiceLayerAlert = () => (
    <LayerTypeAlert
      data-testid="fsl-alert"
      icon={<LayerTypeInfoIcon />}
      action={(
        <FSLButton
          size="small"
          data-testid="fsl-alert-button"
          target="_blank"
          rel="noopener noreferrer"
          href="https://help.fulcrumapp.com/en/articles/7910802-how-do-i-add-a-public-feature-service-layer"
        >
          Learn More
        </FSLButton>
      )}
    >
      Feature Service layers that are not
      publicly accessible should be managed
      in ArcGIS and accessed by
      authenticating within Fulcrum.
    </LayerTypeAlert>
  );
  const renderKMLLayerAlert = () => (
    <LayerTypeAlert
      data-testid="kml-alert"
      icon={<LayerTypeInfoIcon />}
    >
      KML layers exceeding 10MB will not display on the web map. KML layers are only supported on the Esri Map engine.
    </LayerTypeAlert>
  );

  const renderMMPKLayerAlert = () => (
    <LayerTypeAlert
      data-testid="mmpk-alert"
      icon={<LayerTypeInfoIcon />}
    >
      The MMPK layer format is currently restricted to mobile platforms.
      Web rendering for this layer type is not supported at this time.
    </LayerTypeAlert>
  );

  const esriEnabled = app.globals.flags.web_map_engine === 'ESRI';
  const featureServiceLayersEnabled = esriEnabled && layer.type === 'feature-service';
  const showMapPreview = (): boolean => (
    layer.type
      && (layer.type === 'wms' || layer.type === 'xyz' || layer.type === 'fulcrum' || (featureServiceLayersEnabled && esriEnabled))
  );
  const legacyView = <MapView layers={mapViewLayers} />;
  const previewLayerSettings = new LayerSettings(
    [previewLayer],
    previewLayer.source,
    app.globals.config.tile_url,
    app.globals.config.layer_url ?? app.globals.config.tile_url,
    app.preferences,
  );
  const layerToPreview = previewLayerSettings.layerList[0];
  const layerSettingsHandler = LayerSettingsReferenceHandler(previewLayerSettings);
  const plugins = [
    DefaultPlugins(),
    SearchPlugin(),
    Basemap(layerSettingsHandler, app.globals.config.google_maps.api_key),
    PreviewLayer(layerToPreview),
  ];

  const arcGISView = () => <ArcGisMapView plugins={plugins} />;

  return (
    <div className={styles.layerUploader}>
      <div className={styles.layer}>
        <div className={styles.layerForm}>
          <div className="layer-type input">
            <label htmlFor="layer_layer_type">
              <span className={styles.requiredLabelText}>Layer Type</span>
              <div className="layer-type input">
                {isNew ? renderLayerTypeSelect() : renderLayerType()}
              </div>
            </label>
          </div>
          {featureServiceLayersEnabled ? renderFeatureServiceLayerAlert() : null}
          {layer.type === 'kml' ? renderKMLLayerAlert() : null}
          {layer.type === 'mmpk' ? renderMMPKLayerAlert() : null}
          <label htmlFor="layer_name">
            <span className={styles.requiredLabelText}>Name</span>
            <div className="layer-name input">
              <input
                type="text"
                name="layer[name]"
                id="layer_name"
                onChange={(e) => setLayer({ ...layer, name: e.target.value })}
                value={layerName}
                className={`${styles.formControl} span6`}
                required
              />
            </div>
          </label>
          <label htmlFor="layer_description">
            Description
            <div className="layer-description input">
              <textarea
                name="layer[description]"
                id="layer_description"
                onChange={(e) => setLayer({ ...layer, description: e.target.value })}
                className={`${styles.formControl} span6`}
                value={layer.description || ''}
              />
            </div>
          </label>
          <div>
            {renderLayerImporter(false)}
            <input type="hidden" name="layer[key]" value={layer.key || ''} data-testid="layer-key-input" />
            <input type="hidden" name="layer[file_name]" value={layer.file_name || ''} data-testid="layer-file-name-input" />
          </div>
          <hr />
          <PermissionLayers roles={roles} layer_id={!isNew ? layer.id : null} />
        </div>
        {
          showMapPreview()
            && (
              <div className={styles.layerPreview}>
                <span>Layer Preview</span>
                {esriEnabled ? arcGISView() : legacyView}
              </div>
            )
        }
      </div>
      <div className={styles.buttonContainer}>
        {
          showMapPreview()
            && (
              <Button quiet onClick={updatePreview}>
                Update Preview
              </Button>
            )
        }
        <Button quiet link href="/layers">
          Cancel
        </Button>
        <Button affirm onClick={saveLayer} disabled={disabled} title={isNew ? 'Create Layer' : 'Update Layer'}>
          {isNew ? 'Create Layer' : 'Update Layer'}
        </Button>
      </div>

      { renderLoadingState() }
    </div>
  );
};

function getMapViewLayers(layer: Layer, app: App) {
  if (!layer?.type) {
    return [];
  }

  try {
    return [convertFulcrumLayerToMapLayer(layer, app)];
  } catch (error) {
    return [];
  }
}

const styles = css({
  layerUploader: {
    display: 'flex',
    flexDirection: 'column',
    flexBasis: '100%',
    flex: 1,
  },
  layer: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
  },
  layerForm: {
  },
  requiredLabelText: {
    ':after': {
      content: '" *"',
      color: '#a00',
    },
  },
  formControl: {
    fontWeight: 400,
  },
  layerPreview: {
    width: '100%',
    height: 525,
    display: 'flex',
    flexDirection: 'column',
    marginLeft: 20,
  },
  buttonContainer: {
    marginTop: 20,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
});

export default LayerUploader;
